import { useEffect, useRef } from 'react';

import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { Paper, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { CompetitionRanking } from '@scorenco/components';
import { Types } from '@scorenco/core';
import { animate } from 'framer-motion';

import { TeamRankingQueryDocument } from './RankingTab.query.generated';

const style = {
  tab: css`
    width: 100%;
  `,
};

export const RankingTab = ({
  teamId,
  clubId,
}: {
  teamId?: number;
  clubId?: number;
}) => {
  const safeTeamId = teamId ?? 0;
  const safeClubId = clubId ?? 0;
  const scrollRef = useRef<HTMLDivElement>(null);

  const getTeamRankingQuery = useQuery(TeamRankingQueryDocument, {
    variables: {
      teamId: safeTeamId,
      clubId: safeClubId,
    },
    skip: !teamId || !clubId,
    context: {
      role: 'anonymous',
    },
  });

  const rankingCompetition =
    getTeamRankingQuery.data?.teams_team_detail_by_pk?.[0]?.team_in_season
      ?.last_competition_with_ranking?.[0];
  const rankings: Types.Competitions_Ranking_Detail[] =
    rankingCompetition?.rankings?.map((ranking) => ({ ...ranking })) ?? [];
  const sport =
    getTeamRankingQuery.data?.teams_team_detail_by_pk?.[0]?.club?.[0]?.sport;
  const safeSport = sport?.__typename === 'clubs_sport' ? sport : undefined;

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const pauseDuration = 1;
    const scrollDuration = 6;
    let direction: 'down' | 'up' = 'down';
    let controls: ReturnType<typeof animate>;
    let startTimeout: ReturnType<typeof setTimeout>;

    const loop = () => {
      const distance = container.scrollHeight - container.clientHeight;
      if (distance <= 0) {
        return;
      }

      const from = container.scrollTop;
      const to = direction === 'down' ? distance : 0;

      controls = animate(from, to, {
        duration: scrollDuration,
        ease: 'linear',
        onUpdate: (v) => (container.scrollTop = v),
        onComplete: () => {
          direction = direction === 'down' ? 'up' : 'down';
          startTimeout = setTimeout(loop, pauseDuration * 1000);
        },
      });
    };

    startTimeout = setTimeout(loop, pauseDuration * 1000);

    return () => {
      clearTimeout(startTimeout);
      controls?.stop();
    };
  }, [rankings.length]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        variant="h3"
        sx={{ textTransform: 'uppercase', m: 0, flexShrink: 0 }}
      >
        Classement
      </Typography>
      <Typography
        variant="body1"
        sx={{ textDecoration: 'underline', m: 0, mb: 2, flexShrink: 0 }}
      >
        {rankingCompetition?.name}
      </Typography>
      <Paper
        variant="bordered"
        ref={scrollRef}
        sx={{
          minHeight: 0,
          overflow: 'hidden',
          p: 0,
          border: 'none',
        }}
      >
        <CompetitionRanking
          rankings={rankings}
          highlightTeams={teamId ? [teamId] : undefined}
          isClubhouse={true}
          css={style.tab}
          sport={safeSport}
        />
      </Paper>
    </Box>
  );
};
